import { useEffect, useState } from 'react';
import { Trophy } from 'lucide-react';
import { meAPI } from '../lib/api';
import { getLang, t } from '../lib/i18n';

// Points (MMS_POINTS_HUB_PLAN F5). Only the PMS actions: what each is worth,
// how often you've done it, and your latest PMS points. The full picture across
// every product lives in My Account → Points & Rewards.

const fmt = (n) => Math.round(Number(n || 0)).toLocaleString();
const fmtDate = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleDateString(getLang() === 'es' ? 'es' : 'en', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';
};
const panel = 'rounded-lg border border-pm-border bg-pm-surface shadow-[var(--pm-elev-1)]';

function limits(a) {
  const out = [];
  if (a.points_per) out.push(t('per purchase'));
  else if (a.once_per_subject) out.push(t('once each'));
  if (a.caps?.per_day) out.push(`${t('up to')} ${a.caps.per_day} ${t('per day')}`);
  if (a.caps?.total) out.push(`${t('up to')} ${a.caps.total} ${t('in total')}`);
  return out.join(' · ');
}

export default function PointsPage() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    meAPI.points().then(({ data: d }) => setData(d)).catch(() => setFailed(true));
  }, []);

  const empty = (text) => (
    <div className="rounded-lg border border-dashed border-pm-border py-12 text-center text-sm text-pm-muted">{text}</div>
  );

  if (failed) return <div className="p-6">{empty(t('Points are not available right now. Try again in a moment.'))}</div>;
  if (!data) return <p className="p-6 text-sm text-pm-muted">{t('Loading…')}</p>;

  const me = data.me || { points: 0, xp: 0, balance: 0, counts: {}, recent: [] };
  const actions = data.actions || [];
  const names = Object.fromEntries(actions.map((a) => [a.trigger, a.name]));

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6 p-6">
      <div>
        <h1 className="text-xl font-semibold text-pm-text">{t('Points')}</h1>
        <p className="mt-1 text-sm text-pm-muted">{t('What your work in projects earns you.')}</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className={`${panel} p-5`}>
          <div className="text-xs font-semibold uppercase tracking-wide text-pm-faint">{t('Earned in projects')}</div>
          <div className="mt-1.5 flex items-center gap-2 text-[26px] font-bold text-pm-text pm-num"><Trophy size={20} className="text-pm-accent" />{fmt(me.points)}</div>
          <div className="text-xs text-pm-muted pm-num">{fmt(me.xp)} XP</div>
        </div>
        <div className={`${panel} p-5`}>
          <div className="text-xs font-semibold uppercase tracking-wide text-pm-faint">{t('Total balance')}</div>
          <div className="mt-1.5 text-[26px] font-bold text-pm-text pm-num">{fmt(me.balance)}</div>
          <div className="text-xs text-pm-muted">{t('Across every product, in My Account')}</div>
        </div>
        <div className={`${panel} p-5`}>
          <div className="text-xs font-semibold uppercase tracking-wide text-pm-faint">{t('Tasks completed')}</div>
          <div className="mt-1.5 text-[26px] font-bold text-pm-text pm-num">{fmt(me.counts?.pms_task_completed)}</div>
          <div className="text-xs text-pm-muted pm-num">{fmt(me.counts?.pms_share)} {t('shares')}</div>
        </div>
      </div>

      <section>
        <h2 className="mb-1 text-base font-semibold text-pm-text">{t('How to earn points here')}</h2>
        <p className="mb-3 text-sm text-pm-muted">{t('Every action below adds points to your balance. Points are awarded a few seconds after the action.')}</p>
        {actions.length === 0 ? empty(t('No point actions are active in the PMS right now.')) : (
          <div className={`${panel} overflow-x-auto`}>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-pm-border text-left text-xs text-pm-faint">
                  <th className="px-4 py-2.5 font-medium">{t('Action')}</th>
                  <th className="px-3 py-2.5 font-medium">{t('Limits')}</th>
                  <th className="px-3 py-2.5 text-right font-medium">{t('Times')}</th>
                  <th className="px-4 py-2.5 text-right font-medium">{t('Points')}</th>
                </tr>
              </thead>
              <tbody>
                {actions.map((a) => (
                  <tr key={a.id} className="border-b border-pm-border/60 last:border-b-0">
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-pm-text">{t(a.name)}</div>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-pm-muted">{limits(a) || '—'}</td>
                    <td className="px-3 py-2.5 text-right pm-num text-pm-muted">{fmt(me.counts?.[a.trigger])}</td>
                    <td className="px-4 py-2.5 text-right font-semibold pm-num text-pm-text">+{fmt(a.points)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-3 text-base font-semibold text-pm-text">{t('Latest points')}</h2>
        {(me.recent || []).length === 0 ? empty(t('Complete a task to earn your first points here.')) : (
          <div className={`${panel} overflow-x-auto`}>
            <table className="w-full text-sm">
              <tbody>
                {me.recent.map((r) => (
                  <tr key={r.id} className="border-b border-pm-border/60 last:border-b-0">
                    <td className="px-4 py-2.5 text-pm-text">{t(r.rule_name || names[r.event_type] || r.event_type)}</td>
                    <td className="px-3 py-2.5 text-xs text-pm-faint pm-num">{fmtDate(r.created_at)}</td>
                    <td className="px-4 py-2.5 text-right font-semibold pm-num text-pm-text">+{fmt(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
